import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { DownloadAttachmentCommand } from './download-attachment.command';
import { DownloadAttachmentResult } from './download-attachment.result';

/**
 * Download attachment use case.
 * Gets pre-signed download URL for an attachment (60 min TTL).
 *
 * Pure business logic - no state management or error mapping.
 * Presentation layer handles state updates and user-facing error messages.
 */
@Injectable({ providedIn: 'root' })
export class DownloadAttachmentUseCase {
  private readonly _repository = inject(CasesRepository);

  /**
   * Executes retrieval of pre-signed download URL for an attachment.
   * URL is valid for 60 minutes.
   *
   * Pure use case - no state management or side effects. Calling component
   * is responsible for triggering browser download and presenting errors to user.
   *
   * @param command - Download command containing caseId and attachmentId
   * @returns Observable that emits download URL and metadata
   *
   * @throws AttachmentError with appropriate type:
   *   - PERMISSION_DENIED: user lacks download permissions
   *   - NOT_FOUND: attachment doesn't exist
   *   - NETWORK_ERROR: network connectivity issues
   *   - UNKNOWN: unexpected errors
   *
   * Observable behavior:
   *   - Emits once: { downloadUrl, fileName, contentType } for browser download
   *   - Completes: after successful URL retrieval
   *   - Errors: if backend fetch fails (throws AttachmentError)
   *
   * Component responsibilities:
   *   - Trigger browser download using returned URL
   *   - Map errors to user messages via AttachmentErrorMapperService
   *   - Display toast notifications
   *   - No loading state needed (operation is fast)
   */
  public execute(command: DownloadAttachmentCommand): Observable<DownloadAttachmentResult> {
    return this._repository.getAttachmentDownloadUrl(command.caseId, command.attachmentId).pipe(
      map((response) => ({
        downloadUrl: response.downloadUrl,
        fileName: response.fileName,
        contentType: response.contentType,
      }))
    );
  }
}
